'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslation } from 'react-i18next'
import { useWorkspaceScopedLoader, type WorkspaceScopedLoadContext } from '@kiao-voice/hooks'
import { Button, toast } from '@kiao-voice/ui-kit'
import { setActiveContext } from '@kiao-voice/api-client'
import { EMAIL_VERIFICATION_REQUIRED_ERROR, PERSONAL_PAID_PLAN_CODES } from '@kiao-voice/core'
import { APP_ROUTES } from '@/lib/app-routes'
import {
  acceptPendingInvite,
  declinePendingInvite,
  getOrganization,
  listContexts,
  TeamApiError,
} from '@/features/team/api-client'
import {
  EmailVerificationStep,
  type EmailVerificationStepProps,
} from '@/features/auth/email-verification'

/** Personal plan codes that mean the user is PAYING personally (vs. `free`). */
const PAID_PERSONAL_PLAN_CODES = new Set<string>(PERSONAL_PAID_PLAN_CODES)

interface PendingInvite {
  membershipId: number
  orgName: string
}

type InviteReply = 'accept' | 'decline'

interface Props {
  /** Called after an accept/decline so the host page can refresh its data. */
  onChanged?: () => void
  // Test seams.
  fetchOrgFn?: typeof getOrganization
  fetchContextsFn?: typeof listContexts
  acceptFn?: typeof acceptPendingInvite
  declineFn?: typeof declinePendingInvite
  requestVerificationFn?: EmailVerificationStepProps['requestFn']
  verifyFn?: EmailVerificationStepProps['verifyFn']
  navigateToWorkspaceHome?: () => void
}

/**
 * Account-page banners driven by the active-context model:
 *
 * - **Invite banner** — a pending (`invited`) seat the signed-in user hasn't
 *   accepted yet (from `/organizations/me`'s `seatedIn`). Accept/decline run
 *   token-less (the session owns the invited email); accepting switches the
 *   active context into the team they just joined.
 * - **Invite awaiting a mailbox proof** — `/organizations/me` says an invite
 *   waits for this address but the account has not proven it, so it names no
 *   team. The banner verifies the mailbox in place, then re-reads and becomes
 *   the invite banner above. An accept/decline that still answers
 *   `email_verification_required` gets the same in-place step, then repeats the
 *   reply the person chose.
 * - **Double-pay banner** — the user holds a PAID personal plan AND a seat in
 *   someone else's team (derived from `/organizations/contexts`: a paid personal
 *   `planCode` + a team context where they are a `member`). Coexist is allowed,
 *   but the seat already covers full access, so we nudge them that the personal
 *   plan may be redundant. The cancel control lives on this same page.
 *
 * Renders nothing when none of these holds.
 */
export function AccountBanners({
  onChanged,
  fetchOrgFn = getOrganization,
  fetchContextsFn = listContexts,
  acceptFn = acceptPendingInvite,
  declineFn = declinePendingInvite,
  requestVerificationFn,
  verifyFn,
  navigateToWorkspaceHome,
}: Props) {
  const { t } = useTranslation('web')
  const router = useRouter()
  const [pendingInvite, setPendingInvite] = useState<PendingInvite | null>(null)
  const [inviteAwaitingProof, setInviteAwaitingProof] = useState(false)
  const [doublePayOrg, setDoublePayOrg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** The reply that answered `email_verification_required`, while its step is shown. */
  const [verifyingReply, setVerifyingReply] = useState<InviteReply | null>(null)

  const loadWorkspace = useCallback(
    async ({ isCurrent }: WorkspaceScopedLoadContext) => {
      const [org, contexts] = await Promise.all([
        // Both are informational — a failed probe hides the banner (visible
        // recovery) rather than breaking the page, but it's reported, not
        // silently swallowed, so a broken probe is diagnosable.
        fetchOrgFn().catch((err) => {
          reportHandledException(err, { source: 'app/app/account/account-banners.tsx' })
          console.warn('[account-banners] could not resolve org context', err)
          return null
        }),
        fetchContextsFn().catch((err) => {
          reportHandledException(err, { source: 'app/app/account/account-banners.tsx' })
          console.warn('[account-banners] could not resolve contexts', err)
          return null
        }),
      ])

      if (!isCurrent()) return

      const invite =
        org?.seatedIn?.status === 'invited'
          ? { membershipId: org.seatedIn.membershipId, orgName: org.seatedIn.organization.name }
          : null
      setPendingInvite(invite)
      setInviteAwaitingProof(!invite && org?.pendingInviteEmailVerificationRequired === true)

      if (contexts) {
        const personal = contexts.find((context) => context.mode === 'personal')
        const seat = contexts.find(
          (context) => context.mode === 'team' && context.role === 'member'
        )
        const paysPersonally = Boolean(
          personal?.planCode && PAID_PERSONAL_PLAN_CODES.has(personal.planCode)
        )
        setDoublePayOrg(paysPersonally && seat ? seat.name : null)
      } else {
        setDoublePayOrg(null)
      }
    },
    [fetchOrgFn, fetchContextsFn]
  )

  const clearWorkspace = useCallback(() => {
    setPendingInvite(null)
    setInviteAwaitingProof(false)
    setDoublePayOrg(null)
    setVerifyingReply(null)
  }, [])

  const { captureWorkspace, reload } = useWorkspaceScopedLoader({
    load: loadWorkspace,
    clear: clearWorkspace,
  })

  /** Send the chosen reply. The caller owns `busy`. */
  const reply = useCallback(
    async (invite: PendingInvite, choice: InviteReply) => {
      const isCurrentWorkspace = captureWorkspace()
      try {
        if (choice === 'accept') {
          const result = await acceptFn(invite.membershipId)
          if (!isCurrentWorkspace()) return
          // Land the user in the team they just joined.
          setActiveContext(`org:${result.organization.id}`)
          if (navigateToWorkspaceHome) navigateToWorkspaceHome()
          else router.replace(APP_ROUTES.transcriptions)
          toast.success(t('account.inviteBanner.accepted', { org: invite.orgName }))
        } else {
          await declineFn(invite.membershipId)
          if (!isCurrentWorkspace()) return
          toast.success(t('account.inviteBanner.declined'))
        }
        setPendingInvite(null)
        onChanged?.()
      } catch (caughtError) {
        reportHandledException(caughtError, { source: 'app/app/account/account-banners.tsx' })
        if (!isCurrentWorkspace()) return
        // Not a dead end: this account has not proven the invited mailbox yet,
        // so offer the in-place check instead of an error toast.
        if (
          caughtError instanceof TeamApiError &&
          caughtError.code === EMAIL_VERIFICATION_REQUIRED_ERROR
        ) {
          setVerifyingReply(choice)
          return
        }
        toast.error(t('account.inviteBanner.error'))
      }
    },
    [acceptFn, captureWorkspace, declineFn, navigateToWorkspaceHome, onChanged, router, t]
  )

  const runBusy = useCallback(
    async (work: () => Promise<void>) => {
      if (busy) return
      setBusy(true)
      try {
        await work()
      } finally {
        setBusy(false)
      }
    },
    [busy]
  )

  const handleAccept = useCallback(() => {
    if (!pendingInvite) return
    void runBusy(() => reply(pendingInvite, 'accept'))
  }, [pendingInvite, reply, runBusy])

  const handleDecline = useCallback(() => {
    if (!pendingInvite) return
    void runBusy(() => reply(pendingInvite, 'decline'))
  }, [pendingInvite, reply, runBusy])

  // The code is spent once it is accepted, so the step ends here whatever the
  // repeated reply does next; its own failure is reported like any other reply.
  const repeatAfterVerifying = useCallback(async () => {
    const choice = verifyingReply
    setVerifyingReply(null)
    if (!pendingInvite || !choice) return
    await runBusy(() => reply(pendingInvite, choice))
  }, [pendingInvite, reply, runBusy, verifyingReply])

  if (!pendingInvite && !inviteAwaitingProof && !doublePayOrg) {
    return null
  }

  return (
    <>
      {pendingInvite && (
        <div className="callout" role="status" data-testid="account-invite-banner">
          <span className="callout-av" aria-hidden="true">
            {pendingInvite.orgName.trim().charAt(0).toUpperCase() || '?'}
          </span>
          <div className="callout-body">
            <div className="callout-t">
              {t('account.inviteBanner.title', { org: pendingInvite.orgName })}
            </div>
            {verifyingReply ? (
              <EmailVerificationStep
                intro={t('account.inviteBanner.verifyDesc')}
                submitLabel={t(
                  verifyingReply === 'accept'
                    ? 'account.inviteBanner.verifyAndAccept'
                    : 'account.inviteBanner.verifyAndDecline'
                )}
                onVerified={repeatAfterVerifying}
                onCancel={() => setVerifyingReply(null)}
                testIdPrefix="account-invite-verify"
                requestFn={requestVerificationFn}
                verifyFn={verifyFn}
              />
            ) : (
              <div className="callout-d">{t('account.inviteBanner.desc')}</div>
            )}
          </div>
          {verifyingReply ? null : (
            <div className="callout-act">
              <Button
                variant="primary"
                size="sm"
                onClick={handleAccept}
                disabled={busy}
                data-testid="account-invite-accept"
              >
                {t('account.inviteBanner.accept')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDecline}
                disabled={busy}
                data-testid="account-invite-decline"
              >
                {t('account.inviteBanner.decline')}
              </Button>
            </div>
          )}
        </div>
      )}

      {!pendingInvite && inviteAwaitingProof && (
        <div className="callout" role="status" data-testid="account-invite-awaiting-proof">
          <span className="callout-av" aria-hidden="true">
            ?
          </span>
          <div className="callout-body">
            <div className="callout-t">{t('account.inviteBanner.pendingTitle')}</div>
            <EmailVerificationStep
              intro={t('account.inviteBanner.pendingDesc')}
              submitLabel={t('account.inviteBanner.verifyToView')}
              onVerified={reload}
              testIdPrefix="account-invite-verify"
              requestFn={requestVerificationFn}
              verifyFn={verifyFn}
            />
          </div>
        </div>
      )}

      {doublePayOrg && (
        <p
          className="alert show"
          role="status"
          data-testid="account-double-pay-banner"
          style={pendingInvite || inviteAwaitingProof ? { marginTop: 'var(--gap-sm)' } : undefined}
        >
          {t('account.doublePayBanner.body', { org: doublePayOrg })}
        </p>
      )}
    </>
  )
}
